import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import imageCompression from 'browser-image-compression';
import { db, storage } from '../firebase'; // Import storage!
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'; // Import storage functions
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { getImageUrl, getImagePosition } from '../utils/bunnyImage';

// --- Drag-to-position focal point picker ---
// Lets an admin choose what part of a photo stays centered when the site
// crops it to fit the card/modal frames, without touching the source file.
function FocalPointPicker({ imageUrl, initialPosition, onSave, onCancel }) {
  const parseInitial = () => {
    if (initialPosition) {
      const [xStr, yStr] = initialPosition.trim().split(/\s+/);
      const x = parseFloat(xStr);
      const y = parseFloat(yStr);
      if (!Number.isNaN(x) && !Number.isNaN(y)) return { x, y };
    }
    return { x: 50, y: 50 };
  };

  const [pos, setPos] = useState(parseInitial);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromEvent = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    const x = Math.max(0, Math.min(100, ((point.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((point.clientY - rect.top) / rect.height) * 100));
    setPos({ x: Math.round(x), y: Math.round(y) });
  };

  const handleDown = (e) => { draggingRef.current = true; updateFromEvent(e); };
  const handleMove = (e) => { if (draggingRef.current) updateFromEvent(e); };
  const handleUp = () => { draggingRef.current = false; };

  return (
    <div className="modal" onClick={onCancel}>
      <div className="modal-content" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-body">
          <h3 style={{ marginBottom: '8px' }}>Adjust Photo Position</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '15px' }}>
            Click or drag on the photo to choose what stays centered on the site.
          </p>
          <div
            ref={containerRef}
            onMouseDown={handleDown}
            onMouseMove={handleMove}
            onMouseUp={handleUp}
            onMouseLeave={handleUp}
            onTouchStart={handleDown}
            onTouchMove={handleMove}
            onTouchEnd={handleUp}
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4 / 5',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'crosshair',
              backgroundColor: 'var(--petal)',
              touchAction: 'none'
            }}
          >
            <img
              src={imageUrl}
              alt="Adjust position"
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: `${pos.x}% ${pos.y}%`,
                display: 'block',
                pointerEvents: 'none'
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                width: '18px',
                height: '18px',
                border: '2px solid white',
                borderRadius: '50%',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.4), 0 2px 6px rgba(0,0,0,0.4)',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
            <button type="button" className="btn-secondary" style={{ flex: 1 }} onClick={onCancel}>Cancel</button>
            <button type="button" className="btn-primary" style={{ flex: 1 }} onClick={() => onSave(`${pos.x}% ${pos.y}%`)}>Save Position</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [bunnies, setBunnies] = useState([]);
  const [editingId, setEditingId] = useState(null);

  // Newly-selected files, not yet uploaded: { file, previewUrl, position }
  const [pendingImages, setPendingImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Which image the focal point picker is currently open for:
  // { source: 'existing' | 'pending', index } or null when closed
  const [focalPointTarget, setFocalPointTarget] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'TeddyWidder',
    region: 'American',
    gender: 'Buck',
    price: '',
    status: 'available',
    description: '',
    images: [] // Changed back to an array
  });

  const bunniesCollectionRef = collection(db, "bunnies");

  const fetchBunnies = async () => {
    const data = await getDocs(bunniesCollectionRef);
    setBunnies(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
  };

  useEffect(() => {
    fetchBunnies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // NEW: Handle Multiple File Selection
  const handleFileChange = (e) => {
    if (e.target.files) {
      // Revoke any previous local preview URLs before replacing the selection
      pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      const newPending = Array.from(e.target.files).map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        position: '50% 50%'
      }));
      setPendingImages(newPending);
    }
  };

  // Clean up any local preview URLs when the component unmounts.
  // Uses a ref so the cleanup sees the latest selection, not the one from first render.
  const pendingImagesRef = useRef(pendingImages);
  pendingImagesRef.current = pendingImages;
  useEffect(() => {
    return () => {
      pendingImagesRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
  }, []);

  const handleAdjustPosition = (source, index) => {
    setFocalPointTarget({ source, index });
  };

  const handleSavePosition = (newPosition) => {
    if (!focalPointTarget) return;
    const { source, index } = focalPointTarget;

    if (source === 'existing') {
      const updatedImages = formData.images.map((img, i) =>
        i === index ? { url: getImageUrl(img), position: newPosition } : img
      );
      setFormData({ ...formData, images: updatedImages });
    } else {
      setPendingImages((prev) =>
        prev.map((p, i) => (i === index ? { ...p, position: newPosition } : p))
      );
    }
    setFocalPointTarget(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);

    let finalImageUrls = [...formData.images]; // Keep existing images (with any position edits) by default

    // If new files were selected, upload them to Firebase Storage
    if (pendingImages.length > 0) {

      // Compression options
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1000,
        useWebWorker: true,
        fileType: 'image/webp'  // Compress everything to WebP
      };

      const uploadPromises = pendingImages.map(async (pending) => {
        const file = pending.file;
        let fileToUpload = file;
        let finalFileName = `${Date.now()}_${file.name}`;

        // 1. COMPRESS THE FILE
        try {
          fileToUpload = await imageCompression(file, options);
          // Change the extension to .webp for Firebase
          finalFileName = `${Date.now()}_${file.name.split('.')[0]}.webp`;

          console.log(`Shrunk ${file.name} from ${(file.size/1024/1024).toFixed(2)}MB to ${(fileToUpload.size/1024/1024).toFixed(2)}MB`);
        } catch (error) {
          console.error("Compression failed for a file, uploading original.", error);
        }

        // 2. UPLOAD TO FIREBASE
        const imageRef = ref(storage, `bunnies/${finalFileName}`);
        // Tell Firebase it's a webp image
        const metadata = { contentType: 'image/webp' };

        const snapshot = await uploadBytes(imageRef, fileToUpload, metadata);
        const url = await getDownloadURL(snapshot.ref);
        return { url, position: pending.position };
      });

      // Wait for ALL images to finish compressing and uploading
      const newUrls = await Promise.all(uploadPromises);

      // When editing, selecting new images replaces the old ones
      finalImageUrls = newUrls;
    }

    const bunnyDataToSave = {
      name: formData.name,
      type: formData.type,
      region: formData.region,
      gender: formData.gender,
      price: formData.price,
      status: formData.status,
      description: formData.description,
      images: finalImageUrls
    };

    if (editingId) {
      const bunnyDoc = doc(db, "bunnies", editingId);
      await updateDoc(bunnyDoc, bunnyDataToSave);
      setEditingId(null);
    } else {
      await addDoc(bunniesCollectionRef, bunnyDataToSave);
    }

    // Reset Form
    setFormData({ name: '', type: 'TeddyWidder', region: 'American', gender: 'Buck', price: '', status: 'available', description: '', images: [] });
    pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPendingImages([]);
    setIsUploading(false);
    fetchBunnies();
  };

  const handleEdit = (bunny) => {
    setEditingId(bunny.id);
    setFormData({
      name: bunny.name,
      type: bunny.type,
      region: bunny.region || 'American',
      gender: bunny.gender || 'Buck',
      price: bunny.price,
      status: bunny.status,
      description: bunny.description,
      images: bunny.images || []
    });
    pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPendingImages([]);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this bunny profile?")) {
      const bunnyDoc = doc(db, "bunnies", id);
      await deleteDoc(bunnyDoc);
      fetchBunnies();
    }
  };

  return (
    <div className="container" style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Admin Dashboard</h2>
        <button onClick={handleLogout} className="btn-secondary" style={{ padding: '8px 16px', fontSize: '0.9rem' }}>Sign Out</button>
      </div>

      <div style={{ backgroundColor: '#f4f4f4', padding: '30px', borderRadius: '16px', marginBottom: '30px' }}>
        <h3 style={{ marginTop: 0 }}>{editingId ? "Edit Bunny Profile" : "Add New Bunny"}</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

          <input type="text" name="name" placeholder="Bunny Name" value={formData.name} onChange={handleInputChange} required style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} />

          <select name="type" value={formData.type} onChange={handleInputChange} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}>
            <option value="TeddyWidder">TeddyWidder</option>
            <option value="Teddy Dwerg">Teddy Dwerg</option>
          </select>

          <select name="region" value={formData.region} onChange={handleInputChange} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}>
            <option value="American">American</option>
            <option value="European">European</option>
            <option value="American/European">American/European</option>
          </select>

          <select name="gender" value={formData.gender} onChange={handleInputChange} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}>
            <option value="Buck">Buck (Male)</option>
            <option value="Doe">Doe (Female)</option>
          </select>

          <input type="text" name="price" placeholder="Price (e.g., $150)" value={formData.price} onChange={handleInputChange} required style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }} />

          <select name="status" value={formData.status} onChange={handleInputChange} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="waitlist">Waitlist</option>
            <option value="sold">Sold (Moves to Past Babies)</option>
            <option value="breeding">Breeding Stock (Not for sale)</option>
          </select>

          {/* Existing images: adjust their crop/focal point without re-uploading */}
          {editingId && formData.images.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '0.9rem', color: '#476085', fontWeight: 'bold' }}>Current Images:</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {formData.images.map((img, idx) => (
                  <div key={idx} style={{ textAlign: 'center' }}>
                    <img
                      src={getImageUrl(img)}
                      alt={`Current ${idx + 1}`}
                      style={{
                        width: '80px', height: '100px', objectFit: 'cover',
                        objectPosition: getImagePosition(img), borderRadius: '8px', display: 'block'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleAdjustPosition('existing', idx)}
                      style={{ marginTop: '5px', padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer', backgroundColor: '#f0f0f0', border: 'none', borderRadius: '5px' }}
                    >
                      Adjust Position
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* NEW: Native File Input supporting multiple files */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.9rem', color: '#476085', fontWeight: 'bold' }}>
              {editingId ? "Upload New Images (Overwrites existing if selected):" : "Upload Bunny Images:"}
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              required={!editingId}
              style={{ padding: '10px 0', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #ccc', cursor: 'pointer' }}
            />
            <small style={{ color: '#476085' }}>You can select multiple photos at once. Perfect for the image carousel!</small>
          </div>

          {/* Newly-selected images: preview + adjust position before saving */}
          {pendingImages.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {pendingImages.map((pending, idx) => (
                <div key={idx} style={{ textAlign: 'center' }}>
                  <img
                    src={pending.previewUrl}
                    alt={`New upload ${idx + 1}`}
                    style={{
                      width: '80px', height: '100px', objectFit: 'cover',
                      objectPosition: pending.position, borderRadius: '8px', display: 'block'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleAdjustPosition('pending', idx)}
                    style={{ marginTop: '5px', padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer', backgroundColor: '#f0f0f0', border: 'none', borderRadius: '5px' }}
                  >
                    Adjust Position
                  </button>
                </div>
              ))}
            </div>
          )}

          <textarea name="description" placeholder="Brief description..." value={formData.description} onChange={handleInputChange} rows="4" required style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit' }}></textarea>

          <button
            type="submit"
            disabled={isUploading}
            style={{
              backgroundColor: isUploading ? '#ccc' : '#e3bac4',
              color: 'white', padding: '15px', border: 'none', borderRadius: '8px',
              cursor: isUploading ? 'not-allowed' : 'pointer', fontSize: '1rem', fontWeight: 'bold'
            }}
          >
            {isUploading ? "Uploading Images & Saving..." : (editingId ? "Update Bunny" : "Add Bunny")}
          </button>

          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setFormData({ name: '', type: 'TeddyWidder', region: 'American', gender: 'Buck', price: '', status: 'available', description: '', images: [] }); pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl)); setPendingImages([]); }} style={{ backgroundColor: '#07162c', color: 'white', padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
              Cancel Edit
            </button>
          )}
        </form>
      </div>

      <h3>Current Inventory Management</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.04)' }}>
        <thead>
          <tr style={{ backgroundColor: '#07162c', color: 'white' }}>
            <th style={{ padding: '15px' }}>Image</th>
            <th style={{ padding: '15px' }}>Name</th>
            <th style={{ padding: '15px' }}>Status</th>
            <th style={{ padding: '15px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {bunnies.map((bunny) => (
            <tr key={bunny.id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '15px' }}>
                {bunny.images && bunny.images.length > 0 && (
                   <img
                     src={getImageUrl(bunny.images[0])}
                     alt={bunny.name}
                     style={{ width: '60px', height: '60px', objectFit: 'cover', objectPosition: getImagePosition(bunny.images[0]), borderRadius: '8px' }}
                   />
                )}
              </td>
              <td style={{ padding: '15px', fontWeight: 'bold' }}>{bunny.name}</td>
              <td style={{ padding: '15px' }}>{bunny.status?.toUpperCase() || 'UNKNOWN'}</td>
              <td style={{ padding: '15px' }}>
                <button onClick={() => handleEdit(bunny)} style={{ marginRight: '10px', padding: '8px 15px', cursor: 'pointer', backgroundColor: '#f0f0f0', border: 'none', borderRadius: '5px' }}>Edit</button>
                <button onClick={() => handleDelete(bunny.id)} style={{ color: 'white', padding: '8px 15px', cursor: 'pointer', backgroundColor: '#e74c3c', border: 'none', borderRadius: '5px' }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {focalPointTarget && (
        <FocalPointPicker
          imageUrl={
            focalPointTarget.source === 'existing'
              ? getImageUrl(formData.images[focalPointTarget.index])
              : pendingImages[focalPointTarget.index].previewUrl
          }
          initialPosition={
            focalPointTarget.source === 'existing'
              ? getImagePosition(formData.images[focalPointTarget.index])
              : pendingImages[focalPointTarget.index].position
          }
          onSave={handleSavePosition}
          onCancel={() => setFocalPointTarget(null)}
        />
      )}
    </div>
  );
}

export default AdminDashboard;